<template>
    <section class="tw-bg-primary-100 tw-py-16 md:tw-py-20">
        <div class="tw-mx-auto tw-max-w-3xl tw-px-6">
            <div class="tw-mb-10 tw-text-center">
                <h1 class="tw-font-serif tw-text-4xl tw-text-secondary">Pedido de Orçamento</h1>
                <div class="tw-mx-auto tw-mt-4 tw-h-1 tw-w-16 tw-bg-secondary"></div>
                <p class="tw-mt-4 tw-text-gray-600">Conta-me a tua ideia e eu ajudo-te a torná-la doce.</p>
            </div>

            <v-alert
                color="secondary"
                variant="tonal"
                class="tw-mb-8 tw-rounded-xl"
                icon="mdi-information-outline">
                <h3 class="tw-font-serif tw-mb-2 tw-text-lg tw-font-bold">Informações Úteis</h3>
                <ul class="tw-space-y-2 tw-text-sm">
                    <li><strong>Preço Base:</strong> 17€ por Kg (o peso final do bolo pode variar dependendo da massa e recheios escolhidos).</li>
                    <li><strong>Modelagens 3D:</strong> Bonecos e decorações esculpidas à mão têm um valor extra (orçamentado consoante a complexidade).</li>
                    <li><strong>Sabores:</strong> Podes combinar as tuas massas e recheios favoritos nos campos abaixo!</li>
                </ul>
            </v-alert>

            <div
                v-if="inspiration"
                class="tw-mb-8 tw-flex tw-items-center tw-gap-4 tw-rounded-2xl tw-bg-white tw-p-3 tw-shadow-sm">
                <img
                    :src="inspiration.fotoUrl"
                    alt=""
                    class="tw-h-16 tw-w-16 tw-rounded-xl tw-object-cover" />
                <div class="tw-min-w-0 tw-flex-1">
                    <p class="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.16em] tw-text-[#6E3C4B]">Inspiração do portefólio</p>
                    <p class="tw-truncate tw-text-sm tw-text-[#3d2428]">{{ inspiration.name || 'Este bolo' }}</p>
                </div>
                <button
                    type="button"
                    class="tw-text-xs tw-text-gray-500"
                    @click="clearInspiration">
                    Retirar
                </button>
            </div>

            <ol class="tw-mb-8 tw-grid tw-grid-cols-4 tw-gap-2">
                <li
                    v-for="(label, index) in steps"
                    :key="label"
                    class="tw-flex tw-flex-col tw-items-center tw-gap-2">
                    <button
                        type="button"
                        class="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-full tw-text-sm tw-font-semibold"
                        :class="step === index ? 'tw-bg-secondary tw-text-primary-100' : index < step ? 'tw-bg-secondary/15 tw-text-secondary' : 'tw-bg-primary-200 tw-text-gray-500'"
                        @click="goToStep(index)">
                        {{ index + 1 }}
                    </button>
                    <span
                        class="tw-text-center tw-text-[11px] tw-leading-tight sm:tw-text-xs"
                        :class="step === index ? 'tw-font-semibold tw-text-secondary' : 'tw-text-gray-600'">
                        {{ label }}
                    </span>
                </li>
            </ol>

            <v-alert
                v-if="formError"
                type="warning"
                variant="tonal"
                class="tw-mb-6"
                closable
                @click:close="formError = ''">
                {{ formError }}
            </v-alert>

            <v-form
                class="tw-space-y-6"
                @submit.prevent="onSubmit">
                <Transition
                    name="order-step"
                    mode="out-in">
                    <div
                        v-if="step === 0"
                        key="momento"
                        id="order-step">
                        <h2 class="tw-font-serif tw-mb-4 tw-text-2xl tw-text-secondary">O momento</h2>
                        <div class="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-grid-cols-2">
                            <v-text-field
                                v-model="nomePessoa"
                                label="O teu Nome"
                                variant="outlined"
                                color="secondary"
                                placeholder="Ex: Marina Marinho"
                                :error-messages="fieldError(!nomePessoa.trim(), 'Diz-me o teu nome')"
                                required></v-text-field>

                            <v-text-field
                                v-model="nomeBolo"
                                label="Nome para o Bolo"
                                variant="outlined"
                                color="secondary"
                                placeholder="Ex: Parabéns Afonso"
                                :error-messages="fieldError(!nomeBolo.trim(), 'Diz-me o nome para o bolo')"></v-text-field>
                        </div>

                        <div class="tw-mt-6 tw-grid tw-grid-cols-1 tw-gap-6 md:tw-grid-cols-2">
                            <v-text-field
                                v-model="contacto"
                                label="Telemóvel / Contacto"
                                variant="outlined"
                                color="secondary"
                                type="tel"
                                placeholder="912 345 678"
                                prepend-inner-icon="mdi-whatsapp"
                                :error-messages="fieldError(!contacto.trim(), 'Diz-me o contacto')"
                                required></v-text-field>

                            <v-text-field
                                v-model="idade"
                                label="Idade (opcional)"
                                type="number"
                                variant="outlined"
                                color="secondary"
                                placeholder="Ex: 24"></v-text-field>
                        </div>

                        <div class="tw-mt-6">
                            <v-text-field
                                v-model="dataEntrega"
                                label="Data pretendida para a entrega"
                                type="date"
                                variant="outlined"
                                color="secondary"
                                :error-messages="fieldError(!dataEntrega, 'Escolhe a data pretendida')"
                                required></v-text-field>
                        </div>

                        <div class="tw-mt-6">
                            <v-tabs
                                v-model="tab"
                                color="secondary"
                                class="tw-mb-4">
                                <v-tab value="npessoas">Número de Pessoas</v-tab>
                                <v-tab value="peso">Peso</v-tab>
                            </v-tabs>

                            <v-window
                                v-model="tab"
                                class="tw-p-1">
                                <v-window-item value="npessoas">
                                    <v-text-field
                                        v-model="npessoas"
                                        label="Número de Pessoas"
                                        type="number"
                                        variant="outlined"
                                        color="secondary"
                                        :hint="estimativaPesoUI"
                                        :error-messages="fieldError(tab === 'npessoas' && !hasPositive(npessoas), 'Indica o número de pessoas')"
                                        persistent-hint></v-text-field>
                                </v-window-item>
                                <v-window-item value="peso">
                                    <v-text-field
                                        v-model="peso"
                                        label="Peso (Kg)"
                                        type="number"
                                        variant="outlined"
                                        color="secondary"
                                        :hint="estimativaPessoasUI"
                                        :error-messages="fieldError(tab === 'peso' && !hasPositive(peso), 'Indica o peso')"
                                        persistent-hint></v-text-field>
                                </v-window-item>
                            </v-window>
                        </div>
                    </div>

                    <div
                        v-else-if="step === 1"
                        key="ideia"
                        id="order-step">
                        <h2 class="tw-font-serif tw-mb-4 tw-text-2xl tw-text-secondary">A tua ideia</h2>
                        <v-textarea
                            v-model="notas"
                            label="Notas"
                            variant="outlined"
                            color="secondary"
                            rows="6"
                            placeholder="Diz-me as cores, o tema da festa ou qualquer detalhe que queiras..."
                            hint="Quanto mais detalhe, melhor!"
                            :error-messages="fieldError(!notas.trim(), 'Conta-me a tua ideia')"
                            persistent-hint></v-textarea>

                        <div class="tw-mt-6 tw-rounded-lg tw-border-2 tw-border-dashed tw-border-secondary/20 tw-bg-primary tw-p-6">
                            <label class="tw-mb-4 tw-block tw-text-sm tw-font-medium tw-text-secondary"> Inspirações e Referências (Imagens) </label>

                            <v-file-input
                                v-model="files"
                                label="Escolher ficheiros"
                                variant="outlined"
                                prepend-icon="mdi-camera"
                                color="secondary"
                                multiple
                                chips
                                accept="image/*"
                                @update:model-value="onFileChange"></v-file-input>

                            <div
                                v-if="previews.length"
                                class="tw-mt-6 tw-grid tw-grid-cols-3 tw-gap-4 md:tw-grid-cols-4">
                                <div
                                    v-for="(url, index) in previews"
                                    :key="url"
                                    class="tw-relative tw-h-24 tw-w-full tw-overflow-hidden tw-rounded-lg tw-border tw-border-secondary/10">
                                    <v-img
                                        :src="url"
                                        cover
                                        class="tw-h-full tw-w-full"></v-img>
                                    <button
                                        type="button"
                                        class="tw-absolute tw-right-1 tw-top-1 tw-rounded-full tw-bg-secondary tw-p-1 tw-text-primary-100"
                                        :aria-label="'Remover imagem ' + (index + 1)"
                                        @click.prevent="removeImage(index)">
                                        <v-icon size="x-small">mdi-close</v-icon>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="tw-mt-6 tw-grid tw-grid-cols-1 tw-gap-4 tw-rounded-lg tw-bg-primary tw-p-6">
                            <label class="tw-block tw-text-sm tw-font-bold tw-text-secondary">Método de Entrega</label>

                            <v-radio-group
                                v-model="tipoEntrega"
                                inline
                                color="secondary">
                                <v-radio
                                    label="Levantamento na Padaria"
                                    value="levantamento"></v-radio>
                                <v-radio
                                    label="Solicitar Entrega ao Domicílio"
                                    value="entrega"></v-radio>
                            </v-radio-group>

                            <v-expand-transition>
                                <div v-if="tipoEntrega === 'entrega'">
                                    <v-text-field
                                        v-model="moradaEntrega"
                                        label="Morada de Entrega (ou localidade)"
                                        variant="outlined"
                                        color="secondary"
                                        placeholder="Ex: Rua das Flores, nº 10, Porto"
                                        prepend-inner-icon="mdi-map-marker"
                                        :error-messages="fieldError(tipoEntrega === 'entrega' && !moradaEntrega.trim(), 'Indica a morada de entrega')"></v-text-field>
                                    <p class="tw-mt-2 tw-text-xs tw-text-secondary">* Sujeito a taxa de entrega consoante a distância.</p>
                                </div>
                            </v-expand-transition>
                        </div>
                    </div>

                    <div
                        v-else-if="step === 2"
                        key="sabores"
                        id="order-step">
                        <h2 class="tw-font-serif tw-mb-2 tw-text-2xl tw-text-secondary">Os sabores</h2>
                        <p class="tw-mb-4 tw-text-sm tw-text-gray-600">Escolhe uma ou mais massas e recheios. Podes combinar.</p>

                        <p class="tw-mb-3 tw-text-sm tw-font-bold tw-uppercase tw-tracking-widest tw-text-secondary">Massas</p>
                        <p
                            v-if="showErrors && !doughValue.length"
                            class="tw-mb-3 tw-text-sm tw-text-red-700">
                            Escolhe pelo menos uma massa.
                        </p>
                        <div class="tw-grid tw-grid-cols-2 tw-gap-4 sm:tw-grid-cols-4">
                            <button
                                v-for="doughOption in doughs"
                                :key="doughOption.name"
                                type="button"
                                class="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-bg-transparent"
                                :aria-pressed="doughValue.includes(doughOption.name)"
                                @click="toggleSelection(doughValue, doughOption.name)">
                                <span
                                    class="tw-relative tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-black/10"
                                    :class="doughValue.includes(doughOption.name) ? 'tw-ring-2 tw-ring-secondary tw-ring-offset-2' : ''"
                                    :style="{ backgroundColor: doughOption.color }">
                                    <v-icon
                                        v-if="doughValue.includes(doughOption.name)"
                                        size="small"
                                        :color="doughOption.light ? '#6E2432' : '#FBF6E8'">
                                        mdi-check
                                    </v-icon>
                                </span>
                                <span class="tw-text-center tw-text-xs tw-font-medium tw-text-secondary">{{ doughOption.name }}</span>
                            </button>
                        </div>

                        <p class="tw-mb-3 tw-mt-8 tw-text-sm tw-font-bold tw-uppercase tw-tracking-widest tw-text-secondary">Recheios</p>
                        <p
                            v-if="showErrors && !frostingValue.length"
                            class="tw-mb-3 tw-text-sm tw-text-red-700">
                            Escolhe pelo menos um recheio.
                        </p>
                        <div class="tw-flex tw-flex-wrap tw-gap-2">
                            <button
                                v-for="option in frostings"
                                :key="option.name"
                                type="button"
                                class="tw-rounded-full tw-border tw-px-4 tw-py-2 tw-text-sm"
                                :class="frostingValue.includes(option.name) ? 'tw-border-secondary tw-bg-secondary tw-text-primary-100' : 'tw-border-secondary/30 tw-bg-primary tw-text-secondary'"
                                :aria-pressed="frostingValue.includes(option.name)"
                                @click="toggleSelection(frostingValue, option.name)">
                                {{ option.name }}
                            </button>
                        </div>
                    </div>

                    <div
                        v-else
                        key="resumo"
                        id="order-step">
                        <h2 class="tw-font-serif tw-mb-4 tw-text-2xl tw-text-secondary">O resumo</h2>
                        <div class="tw-space-y-3 tw-rounded-xl tw-bg-primary tw-p-6 tw-text-sm tw-leading-relaxed tw-text-gray-700">
                            <p v-if="inspiration"><span class="tw-font-semibold tw-text-secondary">Inspiração:</span> {{ inspiration.name || 'Foto do portefólio' }}</p>
                            <p><span class="tw-font-semibold tw-text-secondary">Nome:</span> {{ nomePessoa || '—' }}</p>
                            <p><span class="tw-font-semibold tw-text-secondary">Contacto:</span> {{ contacto || '—' }}</p>
                            <p><span class="tw-font-semibold tw-text-secondary">Nome no bolo:</span> {{ nomeBolo || '—' }}</p>
                            <p><span class="tw-font-semibold tw-text-secondary">Idade:</span> {{ ageLabel }}</p>
                            <p><span class="tw-font-semibold tw-text-secondary">Data:</span> {{ dataEntrega || '—' }}</p>
                            <p><span class="tw-font-semibold tw-text-secondary">Massa:</span> {{ doughValue.join(', ') || '—' }}</p>
                            <p><span class="tw-font-semibold tw-text-secondary">Recheio:</span> {{ frostingValue.join(', ') || '—' }}</p>
                            <p><span class="tw-font-semibold tw-text-secondary">Número de Pessoas:</span> {{ pessoasResumo }}</p>
                            <p><span class="tw-font-semibold tw-text-secondary">Peso:</span> {{ pesoResumo }}</p>
                            <p><span class="tw-font-semibold tw-text-secondary">Entrega:</span> {{ entregaResumo }}</p>
                            <p class="tw-whitespace-pre-wrap"><span class="tw-font-semibold tw-text-secondary">Notas:</span> {{ notas.trim() || '—' }}</p>
                            <p v-if="files.length"><span class="tw-font-semibold tw-text-secondary">Imagens de referência:</span> {{ files.length }}</p>
                        </div>

                        <div class="tw-mt-6 tw-rounded-xl tw-border tw-border-secondary/15 tw-bg-primary-200 tw-p-5 tw-text-sm tw-text-secondary">
                            <p class="tw-font-semibold">Preço base: 17€/kg.</p>
                            <p class="tw-mt-2">{{ tab === 'peso' ? estimativaPessoasUI : estimativaPesoUI }}</p>
                            <p class="tw-mt-2">Modelagens 3D têm um valor extra, orçamentado consoante a complexidade. Podes combinar massas e recheios.</p>
                        </div>
                    </div>
                </Transition>

                <div class="tw-flex tw-flex-col-reverse tw-gap-3 sm:tw-flex-row sm:tw-justify-between">
                    <v-btn
                        v-if="step > 0"
                        type="button"
                        variant="outlined"
                        color="secondary"
                        size="large"
                        class="tw-w-full tw-text-secondary sm:tw-w-auto"
                        @click="goToStep(step - 1)">
                        Voltar
                    </v-btn>
                    <span
                        v-else
                        class="tw-hidden sm:tw-block"></span>

                    <v-btn
                        v-if="step < steps.length - 1"
                        type="button"
                        variant="flat"
                        color="secondary"
                        size="large"
                        class="tw-w-full tw-text-primary-100 sm:tw-w-auto"
                        @click="continueStep">
                        Continuar
                    </v-btn>
                    <v-btn
                        v-else
                        type="submit"
                        variant="flat"
                        color="secondary"
                        size="large"
                        class="tw-w-full tw-text-primary-100 sm:tw-w-auto"
                        :loading="isSubmitting"
                        :disabled="isSubmitting">
                        Enviar Pedido de Orçamento
                    </v-btn>
                </div>
            </v-form>

            <p class="tw-mt-8 tw-text-center tw-text-xs tw-text-gray-500">* Entraremos em contacto via WhatsApp ou Chamada num prazo de 24h.</p>
        </div>
    </section>

    <v-overlay
        :model-value="isSubmitting"
        class="d-flex align-center justify-center"
        persistent>
        <div class="tw-text-center">
            <v-progress-circular
                color="secondary"
                indeterminate
                size="64"
                width="6"></v-progress-circular>
            <p class="tw-mt-4 tw-font-medium tw-text-white">A processar o teu pedido... 🍰</p>
        </div>
    </v-overlay>

    <v-snackbar
        v-model="snackbar"
        :color="snackbarColor"
        timeout="5000"
        location="top"
        elevation="24">
        <div class="tw-flex tw-items-center tw-gap-2 tw-text-base">
            <v-icon v-if="snackbarColor === 'success'">mdi-check-circle</v-icon>
            <v-icon v-else>mdi-alert-circle</v-icon>
            <span>{{ snackbarText }}</span>
        </div>

        <template v-slot:actions>
            <v-btn
                color="white"
                variant="text"
                icon="mdi-close"
                @click="snackbar = false">
            </v-btn>
        </template>
    </v-snackbar>
</template>

<script lang="ts" setup>
    import { ref, computed, watch, nextTick } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import emailjs from '@emailjs/browser';
    import { createClient } from '@supabase/supabase-js';
    import { doughs, frostings } from '@/data/flavors';

    const route = useRoute();
    const router = useRouter();

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;

    const steps = ['O momento', 'A tua ideia', 'Os sabores', 'O resumo'];
    const step = ref(0);
    const formError = ref('');
    const showErrors = ref(false);
    const isSubmitting = ref(false);
    const inspiration = ref<{ name: string; fotoUrl: string } | null>(null);

    const nomePessoa = ref('');
    const nomeBolo = ref('');
    const contacto = ref('');
    const idade = ref<number | string | null>(null);
    const dataEntrega = ref('');
    const notas = ref('');

    const files = ref<File[]>([]);
    const previews = ref<string[]>([]);
    const inspirationFile = ref<File | null>(null);
    const inspirationFilePrefix = 'inspiracao-portfolio';
    let syncingFiles = false;

    const doughValue = ref<string[]>([]);
    const frostingValue = ref<string[]>([]);

    const npessoas = ref<number | string | null>(null);
    const peso = ref<number | string | null>(null);
    const tab = ref('npessoas');

    const tipoEntrega = ref('levantamento');
    const moradaEntrega = ref('');

    const snackbar = ref(false);
    const snackbarText = ref('');
    const snackbarColor = ref('success');

    const toNumber = (value: number | string | null) => {
        if (value === null || value === '') return null;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const estimativaPesoUI = computed(() => {
        const pessoas = toNumber(npessoas.value);
        if (!pessoas || pessoas <= 0) return 'Calcularemos o peso base (aprox. 150g - 250g/fatia).';
        const minPeso = (pessoas * 0.15).toFixed(1);
        const maxPeso = (pessoas * 0.25).toFixed(1);
        return `Estimativa de Peso: Entre ${minPeso}Kg e ${maxPeso}Kg.`;
    });

    const estimativaPessoasUI = computed(() => {
        const kilos = toNumber(peso.value);
        if (!kilos || kilos <= 0) return 'Calcularemos as fatias base (aprox. 150g - 250g/fatia).';
        const maxPessoas = Math.floor(kilos / 0.15);
        const minPessoas = Math.floor(kilos / 0.25);
        return `Dá para aproximadamente ${minPessoas} a ${maxPessoas} pessoas.`;
    });

    const pessoasResumo = computed(() => {
        if (tab.value === 'npessoas' && toNumber(npessoas.value)) return `${toNumber(npessoas.value)} Pessoas`;
        if (tab.value === 'peso' && toNumber(peso.value)) {
            const kilos = toNumber(peso.value) as number;
            return `Estimativa entre ${Math.floor(kilos / 0.25)} e ${Math.floor(kilos / 0.15)} Pessoas`;
        }
        return '—';
    });

    const pesoResumo = computed(() => {
        if (tab.value === 'npessoas' && toNumber(npessoas.value)) {
            const pessoas = toNumber(npessoas.value) as number;
            return `Estimativa entre ${(pessoas * 0.15).toFixed(1)}Kg e ${(pessoas * 0.25).toFixed(1)}Kg`;
        }
        if (tab.value === 'peso' && toNumber(peso.value)) return `${toNumber(peso.value)}Kg`;
        return '—';
    });

    const entregaResumo = computed(() => (tipoEntrega.value === 'entrega' ? `Entrega em: ${moradaEntrega.value || '—'}` : 'Levantamento na padaria'));

    const ageLabel = computed(() => {
        if (idade.value === null || idade.value === '') return '—';
        return String(idade.value);
    });

    const toggleSelection = (list: string[], value: string) => {
        const index = list.indexOf(value);
        if (index === -1) list.push(value);
        else list.splice(index, 1);
    };

    const refreshPreviews = () => {
        previews.value = [];
        files.value.forEach((file: File) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                if (e.target?.result) previews.value.push(e.target.result as string);
            };
            reader.readAsDataURL(file);
        });
    };

    const onFileChange = () => {
        if (syncingFiles) return;
        const picked = (files.value || []).filter((file) => !file.name.startsWith(inspirationFilePrefix));
        const next = inspirationFile.value ? [inspirationFile.value, ...picked] : picked;
        const same = next.length === (files.value?.length || 0) && next.every((file, index) => file === files.value[index]);
        if (!same) {
            syncingFiles = true;
            files.value = next;
            syncingFiles = false;
        }
        refreshPreviews();
    };

    const removeImage = (index: number) => {
        const file = files.value[index];
        if (file?.name.startsWith(inspirationFilePrefix)) inspirationFile.value = null;
        files.value.splice(index, 1);
        previews.value.splice(index, 1);
    };

    const clearInspiration = () => {
        inspiration.value = null;
        inspirationFile.value = null;
        files.value = files.value.filter((file) => !file.name.startsWith(inspirationFilePrefix));
        refreshPreviews();
        sessionStorage.removeItem('atelier-inspiration');
        if (route.query.inspiracao) router.replace({ name: 'Order' });
    };

    const attachInspirationFile = async (url: string) => {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Não foi possível carregar a foto de inspiração');
        const blob = await response.blob();
        const type = blob.type || 'image/jpeg';
        const ext = type.includes('png') ? 'png' : type.includes('webp') ? 'webp' : 'jpg';
        inspirationFile.value = new File([blob], `${inspirationFilePrefix}.${ext}`, { type });
        onFileChange();
    };

    const loadInspiration = () => {
        if (String(route.query.inspiracao ?? '') !== '1') return;
        const raw = sessionStorage.getItem('atelier-inspiration');
        if (!raw) return;
        try {
            const parsed = JSON.parse(raw) as { name?: string; fotoUrl?: string; kind?: string };
            if (!parsed.fotoUrl) return;
            inspiration.value = { name: parsed.name || '', fotoUrl: parsed.fotoUrl };
            if (!notas.value.trim() && parsed.name) {
                if (parsed.kind === 'miniatura') notas.value = `Gostava de encomendar miniaturas como «${parsed.name}».`;
                else if (parsed.kind === 'bolacha') notas.value = `Gostava de encomendar bolachas como «${parsed.name}».`;
                else notas.value = `Gostava de um bolo como «${parsed.name}».`;
            }
            attachInspirationFile(parsed.fotoUrl).catch((error) => console.error(error));
        } catch {
            sessionStorage.removeItem('atelier-inspiration');
        }
    };

    watch(() => route.fullPath, loadInspiration, { immediate: true });

    const resetForm = () => {
        nomePessoa.value = '';
        nomeBolo.value = '';
        contacto.value = '';
        idade.value = null;
        dataEntrega.value = '';
        notas.value = '';
        files.value = [];
        previews.value = [];
        doughValue.value = [];
        frostingValue.value = [];
        npessoas.value = null;
        peso.value = null;
        tipoEntrega.value = 'levantamento';
        moradaEntrega.value = '';
        step.value = 0;
        formError.value = '';
        showErrors.value = false;
        clearInspiration();
        tab.value = 'npessoas';
    };

    const uploadImagesToSupabase = async (): Promise<string[]> => {
        if (!supabase || files.value.length === 0) return [];

        const urls: string[] = [];

        for (const file of files.value) {
            const fileExt = file.name.split('.').pop();
            const fileName = `${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
            const filePath = `referencias/${fileName}`;

            const { error: uploadError } = await supabase.storage.from('encomendas').upload(filePath, file);

            if (uploadError) {
                console.error('Erro no upload da imagem:', uploadError);
                continue;
            }

            const { data } = supabase.storage.from('encomendas').getPublicUrl(filePath);
            urls.push(data.publicUrl);
        }

        return urls;
    };

    const buildMessage = (imageUrls: string[]) => {
        let message = `Massa: ${doughValue.value.join(', ')}\nRecheio: ${frostingValue.value.join(', ')}\nNúmero de Pessoas: ${pessoasResumo.value}\nPeso: ${pesoResumo.value}\n---\n${entregaResumo.value}`;
        message += `\n\nNotas:\n${notas.value.trim()}`;
        if (inspiration.value) {
            const label = inspiration.value.name || 'Foto do portefólio';
            message = `Inspiração do portefólio: ${label}\n\n${message}`;
        }
        if (imageUrls.length > 0) {
            message += '\n\n---\nIMAGENS DE REFERÊNCIA:\n' + imageUrls.join('\n');
        }
        return message;
    };

    const fieldError = (invalid: boolean, message: string) => (showErrors.value && invalid ? message : '');

    const hasPositive = (value: number | string | null) => {
        const parsed = toNumber(value);
        return parsed !== null && parsed > 0;
    };

    const issuesFor = (index: number) => {
        const missing: string[] = [];
        if (index === 0) {
            if (!nomePessoa.value.trim()) missing.push('o teu nome');
            if (!nomeBolo.value.trim()) missing.push('o nome para o bolo');
            if (!contacto.value.trim()) missing.push('o contacto');
            if (!dataEntrega.value) missing.push('a data pretendida');
            if (tab.value === 'npessoas' && !hasPositive(npessoas.value)) missing.push('o número de pessoas');
            if (tab.value === 'peso' && !hasPositive(peso.value)) missing.push('o peso');
        }
        if (index === 1) {
            if (!notas.value.trim()) missing.push('a descrição da tua ideia');
            if (tipoEntrega.value === 'entrega' && !moradaEntrega.value.trim()) missing.push('a morada de entrega');
        }
        if (index === 2) {
            if (!doughValue.value.length) missing.push('pelo menos uma massa');
            if (!frostingValue.value.length) missing.push('pelo menos um recheio');
        }
        return missing;
    };

    const scrollToStep = () => {
        nextTick(() => {
            const el = document.getElementById('order-step');
            if (!el) return;
            const chrome =
                parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ticker-h')) +
                parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nav-h'));
            const top = el.getBoundingClientRect().top + window.scrollY - (Number.isFinite(chrome) ? chrome : 0) - 16;
            window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        });
    };

    const blockOn = (index: number, missing: string[]) => {
        showErrors.value = true;
        formError.value = `Antes de continuar, falta ${missing.join(', ')}.`;
        step.value = index;
        scrollToStep();
    };

    const continueStep = () => {
        const missing = issuesFor(step.value);
        if (missing.length) {
            blockOn(step.value, missing);
            return;
        }
        showErrors.value = false;
        formError.value = '';
        step.value += 1;
        scrollToStep();
    };

    const goToStep = (index: number) => {
        if (index === step.value || index < 0 || index >= steps.length) return;
        if (index < step.value) {
            showErrors.value = false;
            formError.value = '';
            step.value = index;
            scrollToStep();
            return;
        }
        for (let i = 0; i < index; i += 1) {
            const missing = issuesFor(i);
            if (missing.length) {
                blockOn(i, missing);
                return;
            }
        }
        showErrors.value = false;
        formError.value = '';
        step.value = index;
        scrollToStep();
    };

    const validateOrder = () => {
        for (let i = 0; i < steps.length - 1; i += 1) {
            const missing = issuesFor(i);
            if (missing.length) {
                blockOn(i, missing);
                formError.value = `Antes de enviar, falta ${missing.join(', ')}.`;
                return false;
            }
        }
        formError.value = '';
        return true;
    };

    const onSubmit = () => {
        if (step.value < steps.length - 1) {
            continueStep();
            return;
        }
        submitOrder();
    };

    const submitOrder = async () => {
        if (!validateOrder()) return;

        isSubmitting.value = true;

        try {
            const imageUrls = await uploadImagesToSupabase();
            const templateParams = {
                from_name: nomePessoa.value,
                to_name_bolo: nomeBolo.value,
                contact: contacto.value,
                age: idade.value === null || idade.value === '' ? '—' : String(idade.value),
                date: dataEntrega.value,
                message: buildMessage(imageUrls),
                time: new Date().toLocaleString('pt-PT'),
            };

            const response = await emailjs.send('service_pxu2e5j', 'template_6rfkkbf', templateParams, 'tHUnuJrZW4EFk-xt0');
            console.log('SUCESSO!', response.status, response.text);
            snackbarText.value = 'Pedido enviado com sucesso! Vou responder-te em breve. 🎉';
            snackbarColor.value = 'success';
            snackbar.value = true;
            resetForm();
        } catch (err) {
            console.error('ERRO:', err);
            snackbarText.value = 'Houve um problema ao enviar o pedido. Por favor, tenta novamente.';
            snackbarColor.value = 'error';
            snackbar.value = true;
        } finally {
            isSubmitting.value = false;
        }
    };
</script>

<style scoped>
    .order-step-enter-active,
    .order-step-leave-active {
        transition: opacity 0.28s ease, transform 0.28s ease;
    }

    .order-step-enter-from {
        opacity: 0;
        transform: translateY(10px);
    }

    .order-step-leave-to {
        opacity: 0;
        transform: translateY(-8px);
    }
</style>
